import React, { useContext, useLayoutEffect, useMemo } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import Notifications from "./Notifications/Notifications";
import { PageContext } from "../context/PageContext";
import Grid from "@mui/material/Grid2";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Container from "@mui/material/Container";
import MenuIcon from "@mui/icons-material/Menu";
import PersonIcon from "@mui/icons-material/Person";
import DashboardIcon from "@mui/icons-material/Dashboard";
import SearchIcon from "@mui/icons-material/Search";
import TroubleshootIcon from "@mui/icons-material/Troubleshoot";
import SettingsIcon from "@mui/icons-material/Settings";
import ContactPageIcon from "@mui/icons-material/ContactPage";
import ChatIcon from "@mui/icons-material/Chat";
import { useOutlook } from "../hooks/useOutlook";
import { TenantContext } from "../context/TenantContextProvider";
import { useTenants } from "../hooks/useTenants";

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { title, action } = useContext(PageContext);
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const { isInReadMode } = useOutlook();
  const { tenant } = useContext(TenantContext);
  const { tenants, isLoading } = useTenants();

  const currentTenant = tenants.find((t) => t.id === tenant);

  const isInvoiceUploadEnabled = useMemo(() => !!currentTenant?.documentIntegrationSource, [currentTenant]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  useLayoutEffect(() => {
    if (isInReadMode && sessionStorage.getItem("initialLaunch") === "true") {
      navigate("/contacts");
    } else if (
      sessionStorage.getItem("initialLaunch") === "true" &&
      sessionStorage.getItem("lastLocation") &&
      sessionStorage.getItem("lastLocation") !== location.pathname &&
      sessionStorage.getItem("lastTenant") === tenant
    ) {
      const lastLocation = sessionStorage.getItem("lastLocation") ?? "";
      const lastLocationSearch = sessionStorage.getItem("lastLocationSearch") ?? "";
      navigate(lastLocation + lastLocationSearch);
    }

    sessionStorage.setItem("initialLaunch", "false");
    sessionStorage.setItem("lastLocation", location.pathname);
    sessionStorage.setItem("lastLocationSearch", location.search);
    sessionStorage.setItem("lastTenant", tenant ?? "");
  }, [isInReadMode, location, tenant, navigate]);

  return (
    <>
      <Grid container={true} justifyContent="space-between" alignItems="center" mb={1} p={1}>
        <Grid>
          <IconButton onClick={toggleMenu}>
            <MenuIcon style={{ width: "25px", height: "25px" }} />
          </IconButton>
        </Grid>
        <Grid container={true} alignItems="center" justifyContent="center">
          <Typography component="h1" variant="subtitle1" sx={{ fontSize: "14px" }}>
            {title}
          </Typography>
          {action}
        </Grid>
        <Grid>
          <Notifications />
        </Grid>
      </Grid>

      <Drawer open={isMenuOpen} onClose={toggleMenu} PaperProps={{ sx: { justifyContent: "space-between" } }}>
        <List>
          {currentTenant && (
            <ListItem>
              <ListItemText
                primary={currentTenant.name}
                title={currentTenant.name}
                sx={{
                  maxWidth: "200px",
                  whiteSpace: "normal",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  textAlign: "center",
                }}
              />
            </ListItem>
          )}
          <ListItem disablePadding>
            <ListItemButton component={Link} to="/" onClick={toggleMenu}>
              <ListItemIcon>
                <DashboardIcon />
              </ListItemIcon>
              <ListItemText primary="Dashboard" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton component={Link} to="/chasables/search" onClick={toggleMenu}>
              <ListItemIcon>
                <SearchIcon />
              </ListItemIcon>
              <ListItemText primary="Search Receivables" />
            </ListItemButton>
          </ListItem>
          {!isInvoiceUploadEnabled && (
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/payables/search" onClick={toggleMenu}>
                <ListItemIcon>
                  <TroubleshootIcon />
                </ListItemIcon>
                <ListItemText primary="Search Payables" />
              </ListItemButton>
            </ListItem>
          )}
          {isInReadMode && (
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/activities" onClick={toggleMenu}>
                <ListItemIcon>
                  <ChatIcon />
                </ListItemIcon>
                <ListItemText primary="Activities" />
              </ListItemButton>
            </ListItem>
          )}
          {isInReadMode && (
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/contacts" onClick={toggleMenu}>
                <ListItemIcon>
                  <ContactPageIcon />
                </ListItemIcon>
                <ListItemText primary="Contexts" />
              </ListItemButton>
            </ListItem>
          )}
          <ListItem disablePadding>
            <ListItemButton component={Link} to="/settings" onClick={toggleMenu}>
              <ListItemIcon>
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText primary="Settings" />
            </ListItemButton>
          </ListItem>
        </List>
        <List>
          <ListItem disablePadding>
            <ListItemButton component={Link} to="/profile" onClick={toggleMenu}>
              <ListItemIcon>
                <PersonIcon />
              </ListItemIcon>
              <ListItemText primary="Profile" />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>

      <Container sx={{ flex: "1 1 auto", minHeight: 0 }}>
        <Outlet />
      </Container>
    </>
  );
};

export default Layout;
